import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { KYCFlowState } from '../KYCFlowController';

interface CompletedPreviousScreenProps {
  state: KYCFlowState;
  // Llamado cuando el usuario confirma el resultado y cierra el widget
  onDismiss?: () => void;
  // D-7: disponible solo cuando state.canReset=true (ALLOW_KYC_RESET=true en backend).
  // Llama a resetKycProcess → despacha RESET_KYC → reinicia el flujo desde IDLE.
  onReset?: () => void;
}

// Formatea una fecha ISO8601 al formato largo en español mexicano
function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-MX', { dateStyle: 'long' });
  } catch {
    return iso;
  }
}

// Pantalla D-7: el usuario ya tiene un proceso KYC completado en kyc_users.
// Muestra el resultado anterior y, si ALLOW_KYC_RESET=true, ofrece la opción de reiniciar.
export function CompletedPreviousScreen({ state, onDismiss, onReset }: CompletedPreviousScreenProps) {
  const { previousKycResult } = state;
  const completedAt = previousKycResult?.completedAt ?? null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 5,
        gap: 2,
        textAlign: 'center',
      }}
    >
      <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', opacity: 0.85 }} />

      <Typography variant="h5" fontWeight={700}>
        Tu verificación ya está completa
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
        Ya completaste tu verificación de identidad con Playcity.
        {completedAt ? ` Tu proceso fue aprobado el ${formatDate(completedAt)}.` : ''}
      </Typography>

      {/* Score del proceso anterior si está disponible */}
      {previousKycResult?.score !== null && previousKycResult?.score !== undefined && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} justifyContent="center">
          <Chip
            label={`Score de extracción: ${previousKycResult.score}/100`}
            color="success"
            size="small"
            variant="outlined"
          />
        </Stack>
      )}

      {/* Sección de reinicio — solo visible cuando el backend lo permite */}
      {onReset && (
        <>
          <Divider flexItem sx={{ mt: 2, mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
            Si deseas actualizar tus documentos puedes iniciar una nueva verificación.
            Tu historial anterior quedará reemplazado.
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            onClick={onReset}
            sx={{ mt: 1 }}
          >
            Realizar nueva verificación
          </Button>
        </>
      )}

      {onDismiss && (
        <Button
          variant="contained"
          color="success"
          onClick={onDismiss}
          sx={{ mt: onReset ? 1 : 3 }}
        >
          Continuar
        </Button>
      )}
    </Box>
  );
}
